import { getApiToken, getRefreshHandler } from './token';

// Base URL of the control-plane API (NestJS, served under /v1). Override per-env with VITE_API_URL.
const BASE: string =
  (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:3000/v1';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * The single fetch wrapper every API call goes through. Attaches the bearer token, sets JSON
 * headers, and normalizes errors into ApiError. This is the one seam to swap when auth/transport
 * changes — screens never call fetch directly.
 */
export async function apiFetch<T>(
  path: string,
  options: RequestInit = {},
  // Internal: set on the single post-refresh retry so a persistent 401 can't loop forever.
  retried = false,
): Promise<T> {
  const headers = new Headers(options.headers);
  // Only declare a JSON content-type when a body is actually sent — Fastify rejects an empty body
  // when content-type is application/json (affects bodyless POSTs like reveal/logout).
  if (options.body != null) headers.set('Content-Type', 'application/json');
  const token = getApiToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const res = await fetch(`${BASE}${path}`, { ...options, headers });

  if (!res.ok) {
    // Access token likely expired → try a one-shot refresh, then replay the request once.
    // The auth routes themselves are excluded so a bad login/refresh can't trigger a refresh loop.
    const refreshable =
      res.status === 401 &&
      !retried &&
      path !== '/auth/login' &&
      path !== '/auth/refresh';
    if (refreshable) {
      const refresh = getRefreshHandler();
      if (refresh) {
        // Cap the refresh: a hung /auth/refresh must never freeze the caller (and the UI spinner)
        // forever. On timeout we treat it as failed → the request throws a clean 401 below.
        const refreshed = await Promise.race([
          refresh(),
          new Promise<boolean>((resolve) => setTimeout(() => resolve(false), 8000)),
        ]);
        if (refreshed) return apiFetch<T>(path, options, true);
      }
    }

    let message = res.statusText;
    try {
      const body = (await res.json()) as { message?: string };
      if (body?.message) message = body.message;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(res.status, message);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}
